/** 週活動表單：15 分鐘時間刻度、預覽模型 */

export function buildQuarterHourOptions() {
  const opts = [];
  for (let h = 0; h < 24; h += 1) {
    for (const m of [0, 15, 30, 45]) {
      const hh = String(h).padStart(2, '0');
      const mm = String(m).padStart(2, '0');
      opts.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
    }
  }
  return opts;
}

export function buildHourOptions() {
  const opts = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, '0');
    opts.push({ value: hh, label: hh });
  }
  return opts;
}

export function buildMinuteQuarterOptions() {
  return ['00', '15', '30', '45'].map((mm) => ({ value: mm, label: mm }));
}

export function splitTimeParts(hhmm) {
  const normalized = normalizeTimeToQuarter(hhmm);
  if (!normalized) return { hour: '', minute: '' };
  const [hour, minute] = normalized.split(':');
  return { hour, minute };
}

export function joinTimeParts(hour, minute) {
  const h = String(hour || '').trim();
  const m = String(minute || '').trim();
  if (!h && !m) return '';
  if (!h || !m) return '';
  return normalizeTimeToQuarter(`${h}:${m}`);
}

export function normalizeTimeToQuarter(value) {
  const s = String(value || '').trim().slice(0, 5);
  const m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return '';
  let h = Math.min(23, Math.max(0, parseInt(m[1], 10)));
  let min = parseInt(m[2], 10);
  if (!Number.isFinite(min)) return '';
  min = Math.round(min / 15) * 15;
  if (min >= 60) {
    min = 0;
    h = Math.min(23, h + 1);
  }
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

function pick(obj, ...keys) {
  if (!obj) return '';
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== '') return obj[k];
  }
  return '';
}

/** 將編輯 state / API 列轉成預覽用統一物件 */
export function activityToPreviewModel(source) {
  if (!source) return {};
  const rawDate = pick(source, 'activity_date', 'activityDate', 'ACTIVITY_DATE', 'ACTIVITY_DATE_STR');
  const activityDate = typeof rawDate === 'string' ? rawDate.slice(0, 10) : rawDate;
  const images = source.images
    || (Array.isArray(source.IMAGES) ? source.IMAGES : []);
  return {
    id: source.id ?? source.ID,
    name: pick(source, 'name', 'NAME'),
    activity_date: activityDate,
    activity_time: pick(source, 'activity_time', 'activityTime', 'ACTIVITY_TIME'),
    end_time: pick(source, 'end_time', 'endTime', 'END_TIME'),
    venue_start_time: pick(source, 'venue_start_time', 'venueStartTime', 'VENUE_START_TIME'),
    venue_end_time: pick(source, 'venue_end_time', 'venueEndTime', 'VENUE_END_TIME'),
    venue: pick(source, 'venue', 'VENUE'),
    organizer: pick(source, 'organizer', 'ORGANIZER'),
    detail: pick(source, 'detail', 'DETAIL'),
    activity_flow: pick(source, 'activity_flow', 'activityFlow', 'ACTIVITY_FLOW'),
    consumption_type: pick(source, 'consumption_type', 'consumptionType', 'CONSUMPTION_TYPE'),
    beverage_need: pick(source, 'beverage_need', 'beverageNeed', 'BEVERAGE_NEED'),
    handover_notes: pick(source, 'handover_notes', 'handoverNotes', 'HANDOVER_NOTES'),
    internal_brief: pick(source, 'internal_brief', 'internalBrief', 'INTERNAL_BRIEF'),
    equipment_need: pick(source, 'equipment_need', 'equipmentNeed', 'EQUIPMENT_NEED'),
    occupies_full_venue: !!(source.occupies_full_venue ?? source.occupiesFullVenue ?? source.OCCUPIES_FULL_VENUE),
    is_public: source.is_public !== undefined ? !!source.is_public : (source.IS_PUBLIC !== undefined ? !!source.IS_PUBLIC : true),
    is_internal: source.is_internal !== undefined ? !!source.is_internal : (source.IS_INTERNAL !== undefined ? !!source.IS_INTERNAL : true),
    registration_link: pick(source, 'registration_link', 'registrationLink', 'REGISTRATION_LINK'),
    registrationSlug: pick(source, 'registrationSlug', 'PRODUCT_SLUG', 'productSlug'),
    images: Array.isArray(images) ? images : [],
    venue_color: source.venue_color || source.venueColor || source.VENUE_COLOR || '#4ade80',
  };
}

export function buildEditFromOrganizerDetail(d) {
  if (!d) return {};
  let src = d;
  const hasRevision = !!(d.hasPendingRevision ?? d.HAS_PENDING_REVISION);
  if (hasRevision) {
    const rev = d.pendingRevision
      || (() => {
        try {
          const raw = d.PENDING_REVISION_JSON ?? d.pendingRevisionJson;
          return typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch {
          return null;
        }
      })();
    if (rev?.activity) {
      const a = rev.activity;
      src = {
        ...d,
        NAME: a.name ?? d.NAME,
        ACTIVITY_DATE_STR: a.activity_date ?? d.ACTIVITY_DATE_STR,
        ACTIVITY_DATE: a.activity_date ?? d.ACTIVITY_DATE,
        ACTIVITY_TIME: a.activity_time ?? d.ACTIVITY_TIME,
        END_TIME: a.end_time ?? d.END_TIME,
        VENUE_START_TIME: a.venue_start_time ?? d.VENUE_START_TIME,
        VENUE_END_TIME: a.venue_end_time ?? d.VENUE_END_TIME,
        VENUE: a.venue ?? d.VENUE,
        ORGANIZER: a.organizer ?? d.ORGANIZER,
        DETAIL: a.detail ?? d.DETAIL,
        ACTIVITY_FLOW: a.activity_flow ?? d.ACTIVITY_FLOW,
        CONSUMPTION_TYPE: a.consumption_type ?? d.CONSUMPTION_TYPE,
        REGISTRATION_LINK: a.registration_link ?? d.REGISTRATION_LINK,
        images: Array.isArray(a.images) ? a.images : d.images,
      };
      if (rev.product) {
        const p = rev.product;
        if (p.slug != null) src.PRODUCT_SLUG = p.slug;
        if (p.event_price != null) src.EVENT_PRICE = p.event_price;
        if (p.event_capacity != null) src.EVENT_CAPACITY = p.event_capacity;
        if (p.member_price != null) src.MEMBER_PRICE = p.member_price;
        if (p.payment_method) src.PRODUCT_PAYMENT_METHODS = JSON.stringify([p.payment_method]);
        if (p.product_description != null) src.PRODUCT_DESCRIPTION = p.product_description;
        if (p.product_notes != null) src.PRODUCT_NOTES = p.product_notes;
        if (Array.isArray(p.questions)) src.questions = p.questions;
      }
    }
  }
  const rawDate = pick(src, 'ACTIVITY_DATE_STR', 'ACTIVITY_DATE', 'activityDate');
  const activityDate = typeof rawDate === 'string' ? rawDate.slice(0, 10) : '';
  const pmRaw = src.PRODUCT_PAYMENT_METHODS ?? src.productPaymentMethods;
  let paymentMethod = 'ecpay_credit';
  if (pmRaw) {
    try {
      const arr = typeof pmRaw === 'string' ? JSON.parse(pmRaw) : pmRaw;
      if (Array.isArray(arr) && arr[0]) paymentMethod = arr[0];
    } catch { /* ignore */ }
  }
  const questions = (src.questions || d.questions || []).map((q) => ({
    questionText: q.QUESTION_TEXT ?? q.questionText ?? '',
    questionType: q.QUESTION_TYPE ?? q.questionType ?? 'text',
    isRequired: !!(q.IS_REQUIRED ?? q.isRequired),
  }));
  const activityTime = normalizeTimeToQuarter(pick(src, 'ACTIVITY_TIME', 'activityTime'));
  const activityEnd = normalizeTimeToQuarter(
    pick(src, 'END_TIME', 'endTime', 'end_time'),
  ) || '';
  const venueStart = normalizeTimeToQuarter(
    pick(src, 'VENUE_START_TIME', 'venueStartTime', 'venue_start_time') || activityTime,
  );
  const venueEnd = normalizeTimeToQuarter(
    pick(src, 'VENUE_END_TIME', 'venueEndTime', 'venue_end_time'),
  ) || '';
  return {
    name: pick(src, 'NAME', 'name'),
    activity_date: activityDate,
    activity_time: activityTime || venueStart,
    venue_start_time: venueStart,
    venue_end_time: venueEnd,
    end_time: activityEnd || venueEnd,
    venue: pick(src, 'VENUE', 'venue'),
    organizer: pick(src, 'ORGANIZER', 'organizer'),
    detail: pick(src, 'DETAIL', 'detail'),
    activity_flow: pick(src, 'ACTIVITY_FLOW', 'activity_flow', 'activityFlow'),
    consumption_type: pick(src, 'CONSUMPTION_TYPE', 'consumptionType'),
    beverage_need: pick(d, 'BEVERAGE_NEED', 'beverageNeed'),
    handover_notes: pick(d, 'HANDOVER_NOTES', 'handoverNotes'),
    internal_brief: pick(d, 'INTERNAL_BRIEF', 'internalBrief', 'internal_brief'),
    equipment_need: pick(d, 'EQUIPMENT_NEED', 'equipmentNeed', 'equipment_need'),
    occupies_full_venue: !!(d.OCCUPIES_FULL_VENUE ?? d.occupiesFullVenue),
    ...(() => {
      const on = (v) => v === true || v === 1 || v === '1' || v === 'true';
      const pubOn = on(d.IS_PUBLIC ?? d.isPublic ?? d.is_public);
      const intOn = on(d.IS_INTERNAL ?? d.isInternal ?? d.is_internal);
      const defaultChecked = !pubOn && !intOn;
      return {
        is_public: defaultChecked ? true : pubOn,
        is_internal: defaultChecked ? true : intOn,
      };
    })(),
    registration_link: pick(src, 'REGISTRATION_LINK', 'registrationLink', 'registration_link'),
    images: [...(src.images || d.images || [])],
    slug: pick(src, 'PRODUCT_SLUG', 'productSlug'),
    event_price: src.EVENT_PRICE != null ? String(src.EVENT_PRICE) : (src.eventPrice != null ? String(src.eventPrice) : ''),
    event_capacity: src.EVENT_CAPACITY != null ? String(src.EVENT_CAPACITY) : (src.eventCapacity != null ? String(src.eventCapacity) : ''),
    member_price: src.MEMBER_PRICE != null ? String(src.MEMBER_PRICE) : (src.memberPrice != null ? String(src.memberPrice) : ''),
    payment_method: paymentMethod,
    product_description: pick(src, 'PRODUCT_DESCRIPTION', 'productDescription'),
    product_notes: pick(src, 'PRODUCT_NOTES', 'productNotes'),
    questions,
  };
}

/** 活動來源：主辦審核上架 vs 齊文協助上架 vs 未綁定 */
export function getActivitySourceLabel(source) {
  if (!source) return { kind: 'staff', label: '工作人員新增', detail: '' };
  const oid = source.organizer_id ?? source.organizerId ?? source.ORGANIZER_ID;
  const idNum = oid != null && oid !== '' ? Number(oid) : NaN;
  const unbound = source.organizer_unbound
    || (source.ORGANIZER_ID == null && source.organizer_id == null && !Number.isFinite(idNum));
  if (unbound) {
    return { kind: 'unbound', label: '未綁定主辦', detail: '' };
  }

  const submitted = source.organizer_submitted_at
    ?? source.organizerSubmittedAt
    ?? source.ORGANIZER_SUBMITTED_AT;
  const approved = source.organizer_approved_at
    ?? source.organizerApprovedAt
    ?? source.ORGANIZER_APPROVED_AT;
  const rejectReason = source.organizer_reject_reason
    ?? source.organizerRejectReason
    ?? source.ORGANIZER_REJECT_REASON;
  const status = String(source.status ?? source.STATUS ?? '').toLowerCase();
  const hasRevision = source.has_pending_revision
    ?? source.hasPendingRevision
    ?? source.HAS_PENDING_REVISION;
  const reviewPath = !!(submitted || approved
    || status === 'rejected'
    || (rejectReason && !submitted)
    || hasRevision === true
    || String(hasRevision) === '1');

  const name = pick(
    source,
    'organizer_account_name',
    'organizerAccountName',
    'ORGANIZER_ACCOUNT_NAME',
    'ORGANIZER_DISPLAY_NAME',
    'organizerDisplayName',
  );
  const detail = name ? `（${name}）` : '';

  if (Number.isFinite(idNum) && idNum > 0) {
    if (reviewPath) {
      return { kind: 'organizer', label: '主辦審核上架', detail };
    }
    return { kind: 'assist', label: '齊文協助上架', detail };
  }
  return { kind: 'staff', label: '工作人員新增', detail: '' };
}

export function buildPendingPutBody(edit, extras = {}) {
  const venueStart = edit.venue_start_time || '';
  const venueEnd = edit.venue_end_time || null;
  const activityStart = edit.activity_time || venueStart || '';
  const activityEnd = edit.end_time || null;
  const body = {
    name: edit.name,
    activity_date: edit.activity_date,
    venue_start_time: venueStart || null,
    venue_end_time: venueEnd,
    activity_time: activityStart || null,
    end_time: activityEnd,
    venue: edit.venue,
    organizer: edit.organizer,
    detail: edit.detail,
    activity_flow: edit.activity_flow,
    consumption_type: edit.consumption_type,
    beverage_need: edit.beverage_need,
    handover_notes: edit.handover_notes,
    internal_brief: edit.internal_brief,
    equipment_need: edit.equipment_need,
    occupies_full_venue: edit.occupies_full_venue,
    is_public: !!edit.is_public,
    is_internal: !!edit.is_internal,
    registration_link: edit.registration_link || null,
    images: edit.images,
    slug: edit.slug,
    eventPrice: edit.event_price !== '' ? Number(edit.event_price) : undefined,
    eventCapacity: edit.event_capacity !== '' ? parseInt(edit.event_capacity, 10) : undefined,
    memberPrice: edit.member_price !== '' ? parseInt(edit.member_price, 10) : null,
    paymentMethod: edit.payment_method,
    productDescription: edit.product_description,
    productNotes: edit.product_notes,
    questions: edit.questions,
  };
  if (extras.approval_note !== undefined || extras.approvalNote !== undefined) {
    const note = extras.approval_note ?? extras.approvalNote;
    body.approval_note = note != null ? String(note).trim().slice(0, 500) : '';
  }
  return body;
}
